import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';

import PlacesAutocomplete, {
  geocodeByAddress,
  geocodeByPlaceId,
} from 'react-places-autocomplete';

import FormInput from '../FormInput';
import styles from './AddressAutocomplete.scss';

// let googleSearchOptions = {};
class AddressAutocomplete extends Component {
  static propTypes = {
    invalidAddressText: PropTypes.string,
    required: PropTypes.bool,
    address: PropTypes.string, // address string
    addressData: PropTypes.object,
    onAddressChange: PropTypes.func,
    onAddressFinalized: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    inputStyle: PropTypes.object,
    hideApt: PropTypes.bool,
  };

  static defaultProps = {
    invalidAddressText: 'asdfasdf',
    address: '',
    required: false,
    hideApt: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      address: props.address,
      addressParts: {
        apt: '',
      }, // an address object as output, which contains all address attributes
      dropdownOpen: null,
      // disabled: true, // autocomplete
    };

    this.addressLineFieldRef = React.createRef();
  }

  componentDidMount = () => {
    if (!window.google) {
      window.gmc2 = () => {
        this.init();
      };
    } else {
      this.init();
    }
    this.addressLineFieldRef.current.addEventListener('blur', this.handleBlur);
    this.addressLineFieldRef.current.addEventListener(
      'focus',
      this.handleFocus,
    );

    if (this.props.address) {
      this.mapAddressInput();
    }
  };

  componentWillUnmount = () => {
    if (this.addressLineFieldRef && this.addressLineFieldRef.current) {
      this.addressLineFieldRef.current.removeEventListener(
        'blur',
        this.handleBlur,
      );
      this.addressLineFieldRef.current.removeEventListener(
        'focus',
        this.handleFocus,
      );
    }
  };

  init = () => {
    const SOUTH_WEST_TORONTO = () =>
      new google.maps.LatLng(43.62345, -79.58021);
    const NORTH_EAST_TORONTO = () =>
      new google.maps.LatLng(43.85067, -79.27122);
    this.googleSearchOptions = {
      componentRestrictions: {
        country: 'ca',
      },
      bounds: new google.maps.LatLngBounds(
        SOUTH_WEST_TORONTO(),
        NORTH_EAST_TORONTO(),
      ),
    };
  };

  mapAddressInput = () => {
    let addressInput = this.props.address;
    if (!addressInput) return;
    const unit = addressInput.split('#')[1];
    const apt = unit ? unit.split(',')[0] : '';
    addressInput = addressInput
      .replace(/(#\w*, )/g, '') // strip out unit number
      .replace("'", ''); // strip out apostrophe as they are going to mess up with Google Map Geocoding street names
    this.updateAddress(addressInput, {
      ...this.state.addressParts,
      apt,
    });
    this.handleAddressSelect(
      addressInput,
      get(this.props, 'addressData.google_place_id', null),
    );
  };

  handleBlur = () => {
    const { addressParts, address } = this.state;
    // check if there's a selected address
    if (
      address &&
      address.length > 0 &&
      (!addressParts || !addressParts.address)
    ) {
      this.setState({
        showErrorMsg: true,
        errorMsg: 'Select an address from the dropdown',
      });
    } else if (addressParts && !addressParts.street) {
      this.setState({
        showErrorMsg: true,
        errorMsg: "Street wasn't specified",
      });
    } else if (addressParts && !addressParts.street_number) {
      this.setState({
        showErrorMsg: true,
        errorMsg: "Street number wasn't specified",
      });
    } else {
      this.setState({ showErrorMsg: false, errorMsg: '' });
    }
    this.setState({ dropdownOpen: false }); // close dropdown
  };

  handleFocus = () => {
    this.setState({ dropdownOpen: true });
  };

  handleClear = () => {
    this.setState({ dropdownOpen: false, showErrorMsg: false });
    this.updateAddress(
      '',
      {
        apt: '',
      },
      this.props.onAddressFinalized({
        apt: '',
      }),
    );
  };

  handleAddressChange = address => {
    this.updateAddress(address, {
      ...this.state.addressParts,
    });
  };

  handleAptChange = event => {
    this.updateAddress(
      this.state.address,
      {
        ...this.state.addressParts,
        apt: event.target.value,
      },
      this.state.addressParts.address
        ? this.props.onAddressFinalized({
            ...this.state.addressParts,
            apt: event.target.value,
          })
        : null,
    );
  };

  handleAddressSelect = (address, placeId) => {
    geocodeByPlaceId(placeId)
      .then(([place]) => {
        if (place) {
          const addressParts = this.parsePlace(place, address);
          const selectedAddress = this.generateFormattedAddress(addressParts);
          this.updateAddress(
            selectedAddress,
            addressParts,
            this.props.onAddressFinalized(addressParts),
          );
          this.handleBlur();
        }
      })
      .catch(error => {
        geocodeByAddress(address)
          .then(([place]) => {
            if (place) {
              const addressParts = this.parsePlace(place, address);
              const selectedAddress = this.generateFormattedAddress(
                addressParts,
              );
              this.updateAddress(
                selectedAddress,
                addressParts,
                this.props.onAddressFinalized(addressParts),
              );
              this.handleBlur();
            }
          })
          .catch(error => {
            // this.handleEmptyClick();
          });
      });
  };

  handleAddressError = status => {
    if (status === 'ZERO_RESULTS') {
      this.setState({ empty: true });
    }
  };

  updateAddress = (address, addressParts, callback) => {
    this.setState({ address, addressParts }, () => {
      if (callback) {
        callback();
      }
      this.notifyAddressChange();
    });
  };

  parsePlace = (place, address = null) => {
    const data = {};
    let city;

    data.place_id = place.place_id;
    data.street_number = this.GPlaceExtractFromAdress(
      place.address_components,
      'street_number',
    );
    data.street = this.GPlaceExtractFromAdress(
      place.address_components,
      'route',
    );
    data.address = `${data.street_number} ${data.street}`;

    /** Workaround: Temporary fix for street number not been populated from Google API;
    but provided in dropdown; QP-3798 */
    if (!data.street_number && !!address) {
      const addressStreetNumber = address.split(' ')[0];
      data.street_number = /^\d+$/.test(addressStreetNumber)
        ? addressStreetNumber
        : '';
    }

    /* city can be locality, neighbourhood or political */
    city = this.GPlaceExtractFromAdress(place.address_components, 'locality');

    if (!city) {
      city =
        this.GPlaceExtractFromAdress(
          place.address_components,
          'neighborhood',
        ) ||
        this.GPlaceExtractFromAdress(place.address_components, 'political');
    }
    data.city = city;

    data.province = this.GPlaceExtractFromAdress(
      place.address_components,
      'administrative_area_level_1',
    );
    data.postal = this.GPlaceExtractFromAdress(
      place.address_components,
      'postal_code',
    );
    data.country = this.GPlaceExtractFromAdress(
      place.address_components,
      'country',
    );
    data.geo_lat = place.geometry.location.lat();
    data.geo_long = place.geometry.location.lng();

    data.apt =
      this.state.addressParts.apt ||
      this.GPlaceExtractFromAdress(place.address_components, 'subpremise');

    return data;
  };

  generateFormattedAddress = input => {
    let formatted = '';

    if (input) {
      formatted = input.address;

      if (input.city) {
        formatted += `, ${input.city}`;
      }
      if (input.province) {
        formatted += `, ${input.province}`;
      }
      if (input.postal) {
        formatted += ` ${input.postal}`;
      }
    }

    return formatted;
  };

  GPlaceExtractFromAdress = (components, type) => {
    for (let i = 0; i < components.length; i++) {
      for (let j = 0; j < components[i].types.length; j++) {
        if (components[i].types[j] == type) {
          return components[i].long_name;
        }
      }
    }
    return '';
  };

  notifyAddressChange = () => {
    const { address, addressParts } = this.state;
    const { onAddressChange } = this.props;

    this.setState({ empty: false }); // reset dropdown empty state;

    if (onAddressChange) {
      onAddressChange({
        ...addressParts,
        full_address: address,
        isValid: !!address,
      });
    }
  };

  render() {
    const {
      address,
      addressParts,
      dropdownOpen,
      empty,
      disabled,
      showErrorMsg,
      errorMsg,
    } = this.state;
    const {
      invalidAddressText,
      required,
      placeholder,
      inputStyle,
      hideApt,
    } = this.props;

    return (
      <div className="layout--column" style={{ width: '100%' }}>
        <div className={styles.container}>
          <PlacesAutocomplete
            value={address}
            searchOptions={this.googleSearchOptions}
            onChange={this.handleAddressChange}
            onSelect={this.handleAddressSelect}
            onError={this.handleAddressError}
            debounce={500}
            googleCallbackName="gmc1"
          >
            {({
              getInputProps,
              suggestions,
              getSuggestionItemProps,
              loading,
            }) => (
              <div className="layout--row">
                <div className={styles.dropdownParent}>
                  <FormInput
                    type="text"
                    value={address}
                    {...getInputProps({
                      disabled: !!addressParts && !!addressParts.address,
                      style: { ...inputStyle, marginBottom: 0 },
                      required,
                    })}
                    ref={this.addressLineFieldRef}
                    autocomplete="new-password"
                    label="Your Address"
                  />
                  {address && address.length > 0 && (
                    <div
                      className={styles.addressClear}
                      onClick={this.handleClear}
                    >
                      <span className="icon qm-icon-close" />
                    </div>
                  )}
                  <div
                    className={styles.dropdownWrapper}
                    style={{ display: dropdownOpen ? 'block' : 'none' }}
                  >
                    {loading && (
                      <div className={styles.dropdownLoading}>Loading...</div>
                    )}
                    {suggestions.map(suggestion => (
                      <div
                        {...getSuggestionItemProps(suggestion, {
                          className: styles.dropdownItem,
                        })}
                        key={suggestion.placeId}
                      >
                        <span
                          className={`icon qm-icon-map-icon ${
                            styles.dropdownItemIcon
                          }`}
                        />
                        <span className={styles.dropdownItemMainText}>
                          {suggestion.formattedSuggestion.mainText}
                        </span>
                        <span className={styles.dropdownItemSecondaryText}>
                          {suggestion.formattedSuggestion.secondaryText}
                        </span>
                      </div>
                    ))}
                    {empty && !loading && (
                      <div className={styles.dropdownEmpty}>
                        <span style={{ cursor: 'pointer' }}>
                          Can't find your address...
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                {!hideApt && (
                  <div style={{ width: '100px', marginLeft: '8px' }}>
                    <FormInput
                      type="text"
                      value={addressParts.apt}
                      label="Apt"
                      onChange={this.handleAptChange}
                    />
                  </div>
                )}
              </div>
            )}
          </PlacesAutocomplete>
        </div>

        {showErrorMsg && (
          <span
            className={`layout--row layout-align--start-center ${
              styles.errorMsg
            }`}
          >
            <span
              className={`icon qm-icon-information-circled ${
                styles.errorMsgIcon
              }`}
            />
            {errorMsg}
          </span>
        )}
      </div>
    );
  }
}

export default AddressAutocomplete;
